import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import LandingLayout from "../components/layout/LandingLayout";
import DscsaHero from "../components/lp/DscsaHero";
import DscsaQuiz from "../components/lp/DscsaQuiz";
import HowItWorks from "../components/lp/HowItWorks";
import ManagedContentSections from "../components/lp/ManagedContentSections";
import ProofStory from "../components/lp/ProofStory";
import {
  trackEvent,
  trackServerEvent,
  getUtmParams,
  trackButtonClick,
  setupPageTimeTracking,
  getVisitorId,
  setLandingAttribution,
  trackLandingEvent,
} from "../lib/analytics";
import { DEFAULT_LANDING_PAGE_CONTENT, type LandingPageContent } from "@shared/landingPages";
import { DSCSA_META_DESCRIPTION, DSCSA_META_TITLE } from "@shared/publicPageSeoBuilders";

const LP_PATH = "/lp/dscsa-compliance";
const DEFAULT_ROBOTS = "index,follow,max-image-preview:large,max-snippet:-1";

export default function DscsaLandingPage() {
  const utmRef = useRef(getUtmParams());
  const [content, setContent] = useState<LandingPageContent>(DEFAULT_LANDING_PAGE_CONTENT["dscsa-compliance"]);

  useEffect(() => {
    fetch(`/api/landing-pages/dscsa-compliance?visitorId=${encodeURIComponent(getVisitorId())}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("managed page unavailable")))
      .then((data) => {
        if (data?.content) setContent(data.content as LandingPageContent);
        setLandingAttribution({
          landingPage: LP_PATH,
          variantKey: data?.assignment?.variantKey || "control",
          experimentId: data?.assignment?.experimentId || undefined,
        });
        trackLandingEvent("landing_page_view", LP_PATH);
      })
      .catch(() => {
        setLandingAttribution({ landingPage: LP_PATH, variantKey: "control" });
        trackLandingEvent("landing_page_view", LP_PATH);
      });
    return setupPageTimeTracking(LP_PATH);
  }, []);

  useEffect(() => {
    const existingTags = Array.from(document.head.querySelectorAll<HTMLMetaElement>('meta[name="robots"]'));
    const robotsTag = existingTags[0] ?? document.createElement("meta");
    if (existingTags.length === 0) {
      robotsTag.name = "robots";
      document.head.appendChild(robotsTag);
    }
    existingTags.slice(1).forEach((tag) => tag.remove());
    robotsTag.content = DEFAULT_ROBOTS;
    return () => {
      robotsTag.content = DEFAULT_ROBOTS;
    };
  }, []);

  function handleQuizStep(step: number, answer: string) {
    trackEvent("quiz_step", { step, answer, page: LP_PATH });
    trackServerEvent("dscsa_lp_quiz_step", LP_PATH, { step, answer });
  }

  function handleLeadSubmit() {
    const utm = utmRef.current;
    trackEvent("generate_lead", {
      method: "dscsa_quiz",
      page: LP_PATH,
      ...(utm.utmSource ? { utm_source: utm.utmSource } : {}),
      ...(utm.utmMedium ? { utm_medium: utm.utmMedium } : {}),
      ...(utm.utmCampaign ? { utm_campaign: utm.utmCampaign } : {}),
    });
    trackServerEvent("dscsa_lp_lead_submit", LP_PATH, { method: "dscsa_quiz" });
  }

  function handleCta(location: string) {
    trackLandingEvent("landing_page_cta_click", LP_PATH, {
      buttonLabel: content.ctaText,
      buttonLocation: location,
    });
    trackButtonClick(content.ctaText, location);
  }

  return (
    <LandingLayout ctaText={content.ctaText}>
      <Helmet>
        <title>{DSCSA_META_TITLE} | Premier Pharma</title>
        <meta name="description" content={DSCSA_META_DESCRIPTION} />
        <link rel="canonical" href={`https://premierpharma.com${LP_PATH}`} />
      </Helmet>

      <DscsaHero
        content={content}
        onCtaClick={() => handleCta("hero-cta")}
        onSecondaryClick={() => trackEvent("dscsa_secondary_action", { location: "hero-readiness-link" })}
      />

      <div className="lg:hidden sticky top-[4.5rem] z-40 bg-[hsl(var(--paper)/.96)] backdrop-blur border-b border-[hsl(var(--line))] px-4 py-3">
        <a href={content.ctaDestination} onClick={() => handleCta("dscsa-mobile-bar")} className="btn-primary w-full inline-flex justify-center py-3">
          {content.ctaText}
        </a>
      </div>

      <ProofStory
        pagePath={LP_PATH}
        proofKind="dscsa"
        eyebrow="See the handoff"
        headline="From receiving signal to a documented next step."
        intro="The useful proof is not another compliance claim. It is a visible sequence your team can understand before opening an account."
        mockupVariant="compliance"
        steps={[
          { label: "Receive", title: "Start with product information", body: "Review product identifiers and transaction information as a shipment enters the receiving workflow." },
          { label: "Organize", title: "Keep the record connected", body: "Place transaction information and the team’s review context in one understandable workflow." },
          { label: "Act", title: "Make the next step visible", body: "Give the team a clear place to review follow-up work and retain the relevant record." },
        ]}
      />

      <section className="bg-white py-16 lg:py-20" aria-labelledby="dscsa-problem-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-20 items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-accent">The operational problem</p>
              <h2 id="dscsa-problem-heading" className="text-3xl sm:text-4xl font-bold text-primary mt-3 tracking-tight">Important records should not be scattered across the receiving process.</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                ["01", "Receive", "Review product-identifier information as products arrive."],
                ["02", "Organize", "Keep transaction information and follow-up work easier to find."],
                ["03", "Respond", "Give your team a clearer place to review the next step."],
              ].map(([number, title, body]) => (
                <div key={number} className="border-t-2 border-accent pt-4">
                  <span className="text-xs font-bold text-accent">{number}</span>
                  <h3 className="font-bold text-primary mt-3">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mt-2">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <HowItWorks content={content} onCtaClick={() => handleCta("how-it-works-cta")} />

      <DscsaQuiz onStepAdvance={handleQuizStep} onLeadSubmit={handleLeadSubmit} />

      <ManagedContentSections content={content} pageKind="dscsa" />
    </LandingLayout>
  );
}